import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Factory, Crosshair, Truck, Flask } from "@phosphor-icons/react";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

const applications = [
  {
    id: "habitat",
    icon: <Factory size={32} weight="regular" />,
    title: "Fixed Habitat",
    subtitle: "Primary Habitable Volume",
    description: "Permanent or semi-permanent lunar surface habitats represent the largest enclosed volume where crew spends the majority of operational time. DustSentinel-LX monitors breathing zones, common areas, and crew quarters for dust ingress events originating from airlock cycling, suit maintenance, or material transfer.",
    nodes: ["Breathing zone sensor array", "Common area monitoring node", "Crew quarter deposition witness"],
    challenges: ["Long-duration exposure accumulation", "Multiple ingress vectors from connected modules", "Crew activity correlation required"],
    integration: "Interfaces with habitat ECLSS for filtration control requests and cabin clearing protocols.",
    image: "https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/kfjvtoua_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%282%29.png",
    imageAlt: "DustSentinel-LX sensing node installed in lunar habitat with ControlCore and crew workstation",
  },
  {
    id: "airlock",
    icon: <Crosshair size={32} weight="regular" />,
    title: "Airlock / Suitport",
    subtitle: "Primary Ingress Vector",
    description: "The airlock and suitport threshold is the single most critical dust ingress point in any lunar habitable system. Every EVA return cycle introduces respirable lunar dust into the pressurized volume. DustSentinel-LX provides threshold monitoring to detect, quantify, and drive response to ingress events in real time.",
    nodes: ["Threshold sensing node at pressure boundary", "Vestibule area monitoring", "Post-cycling clearance verification"],
    challenges: ["Highest dust concentration events", "Rapid ingress during pressure equalization", "Repeated cycling degrades baseline"],
    integration: "Interfaces with airlock control systems for timed separation and vestibule clearing sequences.",
    image: "https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/e8wsilmi_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%281%29.png",
    imageAlt: "DustSentinel-LX monitoring astronaut EVA return through airlock with visible dust ingress",
  },
  {
    id: "rover",
    icon: <Truck size={32} weight="regular" />,
    title: "Pressurized Rover",
    subtitle: "Mobile Habitable Volume",
    description: "NASA describes the pressurized rover as a mobile habitat for longer-duration living and working on the lunar surface. DustSentinel-LX provides the same ingress monitoring capability within the rover cabin, adapted for the mobile operational context including docking events and suit donning/doffing cycles.",
    nodes: ["Rover ingress zone sensor", "Cabin breathing zone monitor", "Docking interface threshold node"],
    challenges: ["Vibration and mobility-induced resuspension", "Limited ECLSS capacity vs. fixed habitat", "Docking and undocking events as ingress vectors"],
    integration: "Interfaces with rover environmental control system and telemetry for ground monitoring.",
    image: "https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/94k2zhp2_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%283%29.png",
    imageAlt: "DustSentinel-LX sensing node mounted inside pressurized rover cabin with lunar surface visible",
  },
  {
    id: "sample",
    icon: <Flask size={32} weight="regular" />,
    title: "Sample Handling Zone",
    subtitle: "Contamination-Sensitive Area",
    description: "Zones where regolith samples are transferred, processed, or stored within the pressurized envelope require heightened monitoring. DustSentinel-LX provides contamination-sensitive detection to protect both crew and scientific sample integrity from cross-contamination events.",
    nodes: ["Sample transfer bay sensor", "Processing area monitor", "Storage zone deposition witness"],
    challenges: ["Intentional regolith presence in controlled volumes", "Cross-contamination risk to science samples", "Finer particle detection thresholds required"],
    integration: "Interfaces with sample handling glovebox or fume hood environmental controls.",
  },
];

export default function ApplicationsPage() {
  return (
    <main className="pt-16">
      {/* Header */}
      <section data-testid="applications-hero" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/e8wsilmi_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%281%29.png"
            alt="DustSentinel-LX airlock ingress monitoring"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.15]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0b0d]/80 to-[#0a0b0d]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Applications</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-[#f2f4f8] leading-[1.05] mb-6">
              Deployment Environments
            </h1>
            <p className="text-base md:text-lg text-[#8c9eb3] leading-relaxed max-w-2xl">
              DustSentinel-LX is designed for any pressurized lunar volume where crew health and system integrity depend on dust ingress control.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Details */}
      {applications.map((app, idx) => (
        <section
          key={app.id}
          data-testid={`application-${app.id}`}
          className="py-20 md:py-32 border-b border-[#2d3239]"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Left: Title & Description */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="lg:col-span-2"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-[#4b7399]">{app.icon}</div>
                  <div>
                    <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399]">{app.subtitle}</p>
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8]">{app.title}</h2>
                  </div>
                </div>
                <p className="text-base text-[#8c9eb3] leading-relaxed mb-8">{app.description}</p>

                {/* Featured Image */}
                {app.image && (
                  <div className="relative w-full h-56 md:h-72 mb-8 border border-[#2d3239] overflow-hidden">
                    <img
                      src={app.image}
                      alt={app.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0d]/60 via-transparent to-transparent" />
                  </div>
                )}

                {/* Integration Note */}
                <div className="border-l-2 border-[#4b7399] pl-6 py-2 mb-8">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-1">Integration</p>
                  <p className="text-sm text-[#8c9eb3]">{app.integration}</p>
                </div>
              </motion.div>

              {/* Right: Details */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={1}
                variants={fadeUp}
                className="space-y-6"
              >
                {/* Nodes */}
                <div className="bg-[#121519] border border-[#2d3239] rounded-none p-6">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-4">Sensing Nodes</p>
                  <div className="space-y-2">
                    {app.nodes.map((node) => (
                      <p key={node} className="text-sm text-[#8c9eb3] font-mono">— {node}</p>
                    ))}
                  </div>
                </div>

                {/* Challenges */}
                <div className="bg-[#121519] border border-[#2d3239] rounded-none p-6">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#d97706] mb-4">Key Challenges</p>
                  <div className="space-y-2">
                    {app.challenges.map((ch) => (
                      <p key={ch} className="text-sm text-[#8c9eb3] font-mono">— {ch}</p>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-base text-[#8c9eb3] mb-6">
              Discuss deployment configuration for your specific habitat or vehicle architecture.
            </p>
            <Link
              to="/contact"
              data-testid="applications-cta-button"
              className="bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-8 py-3 rounded-none hover:bg-[#3a5a78] transition-colors inline-flex items-center gap-2"
            >
              Request Architecture Briefing <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
